import React, { useState, useEffect } from "react";
import { useSnackbar } from 'notistack';
import { withRouter } from 'react-router';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import moment from 'moment';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import { Grid, Button, TextField, Dialog, DialogContent, DialogTitle, DialogActions } from '@material-ui/core';
import { globalGetService, globalPostService, globalPutService } from '../../../globalServices';
import { checkApiStatus } from '../../utils_v2';
import { regexConstants } from "../../../constants/regEx";
import { AutocompleteCreatable, PageLoader } from "../../shared_elements";

const AddNonLLP = ({ params, toggleModalFn, addLlp, getResponseBack, assetInfoData }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [llp, setLlp] = useState(addLlp?.mode === 'edit' ? addLlp.data : {});
    const [error, setError] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [modules, setModules] = useState([]);

    useEffect(() => {
        getLLPModule();
    }, []);

    const getLLPModule = () => {
        setLoading(true);
        globalGetService(`console/v2/non-llp-modules/`)
            .then(response => {
                setLoading(false);
                if (checkApiStatus(response)) {
                    setModules(response?.data?.data || []);
                }
            });
    };

    const isDataChanged = () => {
        const original = addLlp?.mode === 'edit' ? (addLlp.data || {}) : {};
        const getModuleName = (mod) => mod?.name || (typeof mod === 'string' ? mod : '') || '';
        return (
            getModuleName(llp?.module) !== getModuleName(original?.module) ||
            (llp?.description || '') !== (original?.description || '') ||
            (llp?.part_number || '') !== (original?.part_number || '') ||
            (llp?.serial_number || '') !== (original?.serial_number || '') ||
            String(llp?.tsn || '') !== String(original?.tsn || '') ||
            String(llp?.csn || '') !== String(original?.csn || '') ||
            String(llp?.cycle_limit || '') !== String(original?.cycle_limit || '') ||
            String(llp?.remaining_cycle || '') !== String(original?.remaining_cycle || '') ||
            (llp?.remarks || '') !== (original?.remarks || '')
        );
    };

    const onFieldChange = (key, value) => {
        if (key === 'csn' || key === 'cycle_limit') {
            let itemData = Object.assign({}, llp);
            if (key === 'csn') {
                itemData = {
                    ...itemData,
                    [key]: value,
                    remaining_cycle: itemData?.cycle_limit ? itemData.cycle_limit - value : '',
                };
            } else {
                itemData = {
                    ...itemData,
                    [key]: value,
                    remaining_cycle: itemData?.csn ? value - itemData.csn : value,
                };
            }
            setLlp(itemData);
        } else {
            setLlp({ ...llp, [key]: value });
        }
    };

    const onAddNonLlp = () => {
        let moduleVal = !llp?.module?.id ? llp?.module?.name : llp?.module;
        let validationInputs = {
            module: moduleVal?.toString()?.trim()?.length > 0 ? '' : 'Please select Module',
            description: llp?.description?.trim()?.length > 0 ? '' : 'Please enter Description',
            part_number: llp?.part_number?.trim()?.length > 0 ? '' : 'Please enter Part Number',
            remaining_cycle: llp?.remaining_cycle?.toString()?.trim()?.length > 0 ? llp?.remaining_cycle >= 0 ? '' : "Cycle Remaining can't be negative value" : '',
        };
        if (Object.keys(validationInputs).every(k => validationInputs[k] === '')) {
            let payload = {
                module_name:  llp.module.name,
                description:llp?.description,
                part_number: llp?.part_number || '',
                serial_number: llp?.serial_number || '',
                tsn: llp?.tsn || null,
                csn: llp?.csn || null,
                cycle_limit: llp?.cycle_limit || '0',
                remaining_cycle: llp?.remaining_cycle || '0',
                remarks:llp?.remarks || '',
                asset_type: assetInfoData?.asset_type?.type,
                asset_id: assetInfoData?.id,
            };
            const base = `console/v2/asset/${params.type}/${params.aircraft_slug}/non-llp/`;
            setLoading(true);
            const request = llp?.id
                ? globalPutService(base + llp.id + '/', payload)
                : globalPostService(base, payload);
            request.then(response => {
                setLoading(false);
                if (checkApiStatus(response)) {
                    enqueueSnackbar(response.data.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                    toggleModalFn();
                    getResponseBack();
                }
            });
        } else {
            setError(validationInputs);
        }
    };

    return (
        <>
            {isLoading ? <PageLoader /> : null}
            <Dialog open={addLlp.modal} className='console-management-modal' maxWidth="md">
                <DialogTitle id="scroll-dialog-title">
                    {llp?.id ? 'Edit' : 'Add'} Non LLP
                </DialogTitle>
                <DialogContent dividers={true}>
                    <div>
                        <Grid container spacing={1}>
                            <Grid item md={4}>
                                <AutocompleteCreatable
                                    required={true}
                                    options={modules}
                                    paramsKey='module'
                                    optionKey='name'
                                    value={llp?.module?.name ? llp.module.name : (llp?.module || '')}
                                    multiple={false}
                                    onFieldChange={(e, paramsKey, newValue) => { onFieldChange('module', newValue); setError({ ...error, module: '' }); }}
                                    label={'Module'}
                                    error={error?.module}
                                    disableClearable={false}
                                    filterSelectedOption={false}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item md={4}>
                                <TextField
                                    required
                                    name='description'
                                    label='Description'
                                    value={llp?.description || ''}
                                    fullWidth
                                    margin="normal"
                                    error={!!error?.description}
                                    helperText={error?.description || ''}
                                    inputProps={{ maxLength: 50 }}
                                    onChange={(e) => onFieldChange('description', e.target.value)}
                                    onFocus={() => setError({ ...error, description: '' })}
                                    InputLabelProps={{ shrink: true }}
                                    variant='outlined'
                                />
                            </Grid>
                            <Grid item md={4}>
                                <TextField
                                    required
                                    name='part_number'
                                    label='Part Number'
                                    value={llp?.part_number || ''}
                                    fullWidth
                                    margin="normal"
                                    error={!!error?.part_number}
                                    helperText={error?.part_number || ''}
                                    inputProps={{ maxLength: 15 }}
                                    onChange={(e) => onFieldChange('part_number', e.target.value)}
                                    onFocus={() => setError({ ...error, part_number: '' })}
                                    InputLabelProps={{ shrink: true }}
                                    variant='outlined'
                                />
                            </Grid>
                            <Grid item md={4}>
                                <TextField
                                    name='serial_number'
                                    label='Serial Number'
                                    value={llp?.serial_number || ''}
                                    fullWidth
                                    inputProps={{ maxLength: 15 }}
                                    margin="normal"
                                    onChange={(e) => onFieldChange('serial_number', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    variant='outlined'
                                />
                            </Grid>
                            <Grid item md={4}>
                                <TextField
                                    name='tsn'
                                    label='TSN'
                                    value={llp?.tsn === '0' ? '0' : llp?.tsn || ''}
                                    fullWidth
                                    inputProps={{ maxLength: 10 }}
                                    margin="normal"
                                    onChange={(e) => regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('tsn', e.target.value) : e.preventDefault()}
                                    InputLabelProps={{ shrink: true }}
                                    variant='outlined'
                                />
                            </Grid>
                            <Grid item md={4}>
                                <TextField
                                    name='csn'
                                    label='CSN'
                                    value={llp?.csn === '0' ? '0' : llp?.csn || ''}
                                    fullWidth
                                    inputProps={{ maxLength: 10 }}
                                    margin="normal"
                                    onChange={(e) => regexConstants.onlyNumeric.test(e.target.value) ? onFieldChange('csn', e.target.value) : e.preventDefault()}
                                    InputLabelProps={{ shrink: true }}
                                    variant='outlined'
                                />
                            </Grid>
                            <Grid item md={4}>
                                <TextField
                                    name='cycle_limit'
                                    label='Cycle Limit'
                                    value={llp?.cycle_limit === '0' ? '0' : llp?.cycle_limit || ''}
                                    fullWidth
                                    inputProps={{ maxLength: 10 }}
                                    margin="normal"
                                    onChange={(e) => regexConstants.numberNoSpace.test(e.target.value) ? onFieldChange('cycle_limit', e.target.value) : e.preventDefault()}
                                    InputLabelProps={{ shrink: true }}
                                    variant='outlined'
                                />
                            </Grid>
                            <Grid item md={4}>
                                <TextField
                                    name='remaining_cycle'
                                    label='Remaining Cycles'
                                    value={llp?.remaining_cycle === '0' ? '0' : llp?.remaining_cycle || ''}
                                    fullWidth
                                    inputProps={{ maxLength: 10 }}
                                    margin="normal"
                                    disabled={['engine'].includes(params?.type) && !error?.remaining_cycle}
                                    InputProps={{ readOnly: ['engine'].includes(params?.type) }}
                                    error={!!error?.remaining_cycle}
                                    helperText={error?.remaining_cycle || ''}
                                    onChange={(e) => regexConstants.numberNoSpace.test(e.target.value) ? onFieldChange('remaining_cycle', e.target.value) : e.preventDefault()}
                                    onFocus={() => setError({ ...error, remaining_cycle: '' })}
                                    InputLabelProps={{ shrink: true }}
                                    variant='outlined'
                                />
                            </Grid>
                            <Grid item md={12}>
                                <TextField
                                    name='remarks'
                                    label='Remarks'
                                    value={llp?.remarks || ''}
                                    multiline
                                    rows={3}
                                    fullWidth
                                    margin="normal"
                                    onChange={(e) => onFieldChange('remarks', e.target.value)}
                                    inputProps={{ maxLength: 255 }}
                                    InputLabelProps={{ shrink: true }}
                                    variant='outlined'
                                />
                            </Grid>
                        </Grid>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={toggleModalFn} color='primary' size='small' variant='outlined'>Cancel</Button>
                    <Button disabled={!isDataChanged} onClick={onAddNonLlp} color='primary' size='small' variant='contained'>Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default withRouter(AddNonLLP);
